import { useState } from "react";
import { motion } from "framer-motion";
import { Plus, Check } from "lucide-react";
import { toast } from "sonner";
import { ORIGINAL_FLAVORS, PREMIUM_FLAVORS, PRICES, SIZE_LABELS } from "@/data";
import { useStore } from "@/context/StoreContext";

const FlavorCard = ({ flavor, tier }) => {
  const { addItem, setCartOpen } = useStore();
  const [size, setSize] = useState("large");
  const price = PRICES[tier][size];

  const handleAdd = () => {
    addItem(flavor, size, price);
    toast.success(`${flavor.name} (${SIZE_LABELS[size]}) added`, {
      action: { label: "View Cart", onClick: () => setCartOpen(true) },
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 26 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.5 }}
      className="glass group relative rounded-2xl p-5 transition-transform duration-300 hover:-translate-y-1.5"
      data-testid={`flavor-card-${flavor.id}`}>
      <div className="absolute inset-x-5 top-0 h-px bg-gradient-to-r from-transparent via-white/25 to-transparent" />
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="h-11 w-11 shrink-0 rounded-full border border-white/20" style={{
            background: `radial-gradient(circle at 35% 30%, ${flavor.color}, ${flavor.color}22 75%)`,
            boxShadow: `0 0 24px ${flavor.color}66`,
          }} />
          <div>
            <h3 className="font-display text-lg font-bold text-white leading-tight">{flavor.name}</h3>
            <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-slate-400">{flavor.tag}</span>
          </div>
        </div>
        <span className="font-display text-xl font-black text-white">${price}</span>
      </div>

      <p className="mt-3 text-sm text-slate-400 leading-relaxed min-h-[40px]">{flavor.desc}</p>

      <div className="mt-4 flex items-center gap-2">
        {["small", "large"].map((s) => (
          <button key={s} data-testid={`size-${flavor.id}-${s}`} onClick={() => setSize(s)}
            className={`flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors duration-300 ${
              size === s ? "bg-white text-cosmic-base" : "bg-white/5 text-slate-300 hover:bg-white/10"}`}>
            {size === s && <Check size={12} />}
            {SIZE_LABELS[s]} · ${PRICES[tier][s]}
          </button>
        ))}
      </div>

      <button data-testid={`add-to-cart-${flavor.id}`} onClick={handleAdd}
        className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-cosmic-pink to-cosmic-purple py-2.5 text-sm font-bold text-white transition-transform duration-300 hover:scale-[1.03]">
        <Plus size={15} /> Add to Order
      </button>
    </motion.div>
  );
};

export const FlavorMenu = () => {
  const [tab, setTab] = useState("original");
  const flavors = tab === "original" ? ORIGINAL_FLAVORS : PREMIUM_FLAVORS;

  return (
    <section id="flavors" data-testid="flavor-menu-section" className="relative py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="max-w-2xl">
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-cosmic-cyan">The Menu</span>
          <h2 className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white">
            Pick your <span className="text-gradient-candy">cloud</span>
          </h2>
          <p className="mt-4 text-base text-slate-400 leading-relaxed">
            Originals at $8 large / $4 small. Premium candy-fusion flavors at $10 large / $5 small.
            Every container spun fresh to order.
          </p>
        </div>

        <div className="mt-10 inline-flex rounded-full glass p-1.5" data-testid="flavor-tabs">
          {[["original", "Original · $8 / $4"], ["premium", "Premium · $10 / $5"]].map(([key, label]) => (
            <button key={key} data-testid={`flavor-tab-${key}`} onClick={() => setTab(key)}
              className={`rounded-full px-6 py-2.5 text-sm font-bold transition-colors duration-300 ${
                tab === key ? "bg-gradient-to-r from-cosmic-pink to-cosmic-purple text-white" : "text-slate-300 hover:text-white"}`}>
              {label}
            </button>
          ))}
        </div>

        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-testid="flavor-grid">
          {flavors.map((f) => <FlavorCard key={f.id} flavor={f} tier={tab} />)}
        </div>

        {tab === "premium" && (
          <p className="mt-8 text-sm text-slate-400" data-testid="bulk-note">
            Hosting an event? Premium flavors are available in <span className="text-cosmic-yellow font-semibold">bulk tubs</span> with any live spinning booking.
          </p>
        )}
      </div>
    </section>
  );
};

export default FlavorMenu;
